import { ReactNode } from "react";
import { faPlusCircle } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import { Optional } from "common/utils/types";
import Icon from "common/components/Icon";

import SelectButton, {
  RenderContentProps,
  SelectButtonProps,
} from "client/common/components/buttons/SelectButton";

import Label from "./Label";

import styles from "../../styles/SelectPanel.module.less";

const noop = () => undefined;

// hardcoded list of supported values to support default call to `.toString()` for `getKey`
type SupportedValues = object;

type RenderAdd = ({ icon }: { icon: ReactNode }, selectProps: RenderContentProps) => ReactNode;

type AddSelectButtonProps = {
  entityName: string;
  onClick: () => void;
  renderLabel?: RenderAdd;
  disabled: boolean;
  align?: "start" | "center";
};

const AddSelectButton = ({
  entityName,
  onClick,
  renderLabel,
  disabled,
  align,
}: AddSelectButtonProps) => {
  const icon = <Icon icon={faPlusCircle} />;
  return (
    <SelectButton
      align={align}
      isActive={false}
      disabled={disabled}
      onClick={onClick}
      className={cx(styles.option, styles.addOption)}
      renderContent={(props) =>
        renderLabel ? (
          renderLabel({ icon }, props)
        ) : (
          <div className={styles.addSelectButton}>
            {icon}
            Add New {entityName}
          </div>
        )
      }
    />
  );
};

type ButtonState = Partial<
  Pick<SelectButtonProps, "isLoading" | "disabled" | "renderExpandedContent">
>;

type AddPlacementType = "top" | "bottom";
type RequiredAddProps = { hasAdd: true; onAdd: () => void } | { hasAdd?: false };

type SelectPanelInputProps<T extends SupportedValues> = {
  entityName?: string;
  getKey: (value: Optional<T>) => string | number | boolean;
  getState?: (value: T) => Optional<ButtonState>;
  label?: string;
  options: T[];
  renderOption: (value: T, props: RenderContentProps) => ReactNode;
  value: Optional<T>;
  onChange?: (value: T) => void;
  onClick?: (value: T) => void;
  hasAdd?: boolean;
  renderAdd?: ({ icon }: { icon: ReactNode }) => ReactNode;
  onAdd?: () => void;
  addPlacement?: AddPlacementType;
  isAddDisabled?: boolean;
  variant?: "vertical" | "horizontal";
  disabled?: boolean;
} & RequiredAddProps;

/**
 * TODO sc16248 is it worth adapting 2D selects like this to fit into this component (eg. product size select)
 *  - https://app.shortcut.com/vetcove/story/16248/update-select-panels-across-the-app-add-support-for-2d-panels ?
 * TODO sc24782 - This component needs support for `errors` and all consumers should be updated where relevant!
 * */
const SelectPanel = <T extends SupportedValues>({
  entityName = "",
  getKey,
  getState = () => ({}),
  label,
  options,
  renderOption,
  value,
  onChange = noop,
  onClick = noop,
  hasAdd,
  renderAdd,
  onAdd = noop,
  isAddDisabled = false,
  variant = "vertical",
  addPlacement = "bottom",
  disabled = false,
}: SelectPanelInputProps<T>) => {
  const valueKey = getKey(value);
  const handleClick = (option: T) => {
    onClick(option);
    if (getKey(value) !== getKey(option)) {
      onChange(option);
    }
  };

  const displayedOptions = options.map((option) => {
    const isActive = valueKey === getKey(option);

    return (
      <SelectButton
        {...getState(option)}
        key={`${getKey(option)}`}
        isActive={isActive}
        onClick={() => handleClick(option)}
        renderContent={(props) => renderOption(option, props)}
        className={cx(styles.option, isActive && styles.active)}
        disabled={disabled}
      />
    );
  });

  return (
    <div className={cx(styles.container, styles[variant])}>
      {label && <Label>{label}</Label>}
      <div className={styles.options}>
        {addPlacement === "bottom" && displayedOptions}
        {hasAdd && (
          <AddSelectButton
            disabled={isAddDisabled}
            entityName={entityName}
            onClick={onAdd}
            renderLabel={renderAdd}
          />
        )}
        {addPlacement === "top" && displayedOptions}
      </div>
    </div>
  );
};

export default SelectPanel;
